import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeDayName, getBackendDayFormat } from '../../../../utils/dayUtils';
import { 
    TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Button, CircularProgress,
    Box, Typography, Grid, InputAdornment, Paper, ToggleButtonGroup, ToggleButton, Alert, Divider,
    Chip, Card, CardContent, IconButton, Tooltip, Snackbar, Switch, Popover, useMediaQuery,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useSalonsListQuery, useTeamsTeamMembersSalonMembersRetrieveQuery, useSchedulesSchedulesCreateMutation, useAuthProfileRetrieveQuery, useSalonsRetrieveQuery, useSchedulesBreaksCreateMutation } from '../../../../services/GLOWClient';
import { theme } from '../../../../theme';
import MuiDialog from '../../../../SystemDesign/MuiDialog';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CoffeeIcon from '@mui/icons-material/Coffee';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

import { 
    AdapterDayjs 
} from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker, MultiSectionDigitalClock } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import AddBreak from '../breaks/AddBreak';

const AddSchedule = ({ open, onClose, onSuccess, selectedSalonId = null, selectedTeamMemberId = null }) => {
    const { t } = useTranslation();
   
    // Determine if this is creation mode (when selectedTeamMemberId is provided)
    const isCreationMode = !!selectedTeamMemberId;

    const isFullScreen = useMediaQuery('(max-width:900px)');
    
    // Time picker popover state
    const [timePickerAnchor, setTimePickerAnchor] = useState(null);
    const [activeTimePicker, setActiveTimePicker] = useState(null);

    const normalizeClosedToken = (value) => {
        if (value === null || value === undefined) return '';
        return String(value)
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '');
    };

    const isClosedTime = (value) => {
        const v = normalizeClosedToken(value);
        return v === '' || v === 'ferme' || v === 'fermee' || v === 'closed';
    };

    const getSalonHoursForDay = (salonData, dayId) => {
        const openingHours = salonData?.opening_hours;
        if (!openingHours) return null;

        const keyMap = {
            lun: ['lundi', 'Lundi', 'monday', 'Monday'],
            mar: ['mardi', 'Mardi', 'tuesday', 'Tuesday'],
            mer: ['mercredi', 'Mercredi', 'wednesday', 'Wednesday'],
            jeu: ['jeudi', 'Jeudi', 'thursday', 'Thursday'],
            ven: ['vendredi', 'Vendredi', 'friday', 'Friday'],
            sam: ['samedi', 'Samedi', 'saturday', 'Saturday'],
            dim: ['dimanche', 'Dimanche', 'sunday', 'Sunday', 'sun', 'Sun'],
        };

        const candidates = keyMap[dayId] || [];
        for (const key of candidates) {
            if (openingHours[key]) return openingHours[key];
        }

        return null;
    };

    const isSalonOpenOnDay = (salonData, dayId) => {
        const hours = getSalonHoursForDay(salonData, dayId);
        if (!hours) return false;
        return !isClosedTime(hours.ouverture) && !isClosedTime(hours.fermeture);
    };

    // Function to get default schedule times from salon opening hours
    const getDefaultScheduleTimes = (salonData) => {
        if (!salonData?.opening_hours) {
            return {
                Lundi: { start_time: '', end_time: '' },
                Mardi: { start_time: '', end_time: '' },
                Mercredi: { start_time: '', end_time: '' },
                Jeudi: { start_time: '', end_time: '' },
                Vendredi: { start_time: '', end_time: '' },
                Samedi: { start_time: '', end_time: '' },
                Dimanche: { start_time: '', end_time: '' }
            };
        }
        
        const dayMapping = {
            'lundi': 'Lundi',
            'mardi': 'Mardi', 
            'mercredi': 'Mercredi',
            'jeudi': 'Jeudi',
            'vendredi': 'Vendredi',
            'samedi': 'Samedi',
            'dimanche': 'Dimanche'
        };
        
        const scheduleTimes = {};
        
        Object.entries(dayMapping).forEach(([salonDay, scheduleDay]) => {
            const dayId = {
                lundi: 'lun',
                mardi: 'mar',
                mercredi: 'mer',
                jeudi: 'jeu',
                vendredi: 'ven',
                samedi: 'sam',
                dimanche: 'dim',
            }[salonDay];

            const salonHours = getSalonHoursForDay(salonData, dayId);
            if (salonHours && !isClosedTime(salonHours.ouverture) && !isClosedTime(salonHours.fermeture)) {
                scheduleTimes[scheduleDay] = {
                    start_time: salonHours.ouverture,
                    end_time: salonHours.fermeture
                };
            } else {
                scheduleTimes[scheduleDay] = {
                    start_time: '',
                    end_time: ''
                };
            }
        });
        
        return scheduleTimes;
    };
    
    // Function to get days where salon is open (not closed)
    const getOpenDays = (salonData) => {
        if (!salonData?.opening_hours) {
            return ['lun', 'mar', 'mer', 'jeu', 'ven']; // Default to weekdays
        }
        
        const dayMapping = {
            'lundi': 'lun',
            'mardi': 'mar', 
            'mercredi': 'mer',
            'jeudi': 'jeu',
            'vendredi': 'ven',
            'samedi': 'sam',
            'dimanche': 'dim'
        };
        
        const openDays = [];
        
        Object.entries(dayMapping).forEach(([salonDay, dayId]) => {
            if (isSalonOpenOnDay(salonData, dayId)) {
                openDays.push(dayId);
            }
        });
        
        return openDays.length > 0 ? openDays : ['lun', 'mar', 'mer', 'jeu', 'ven'];
    };
    
    // Default schedule times (will be updated when salon data loads)
    const defaultScheduleTimes = {
        Lundi: { start_time: '', end_time: '' },
        Mardi: { start_time: '', end_time: '' },
        Mercredi: { start_time: '', end_time: '' },
        Jeudi: { start_time: '', end_time: '' },
        Vendredi: { start_time: '', end_time: '' },
        Samedi: { start_time: '', end_time: '' },
        Dimanche: { start_time: '', end_time: '' }
    };
    
    const [formData, setFormData] = useState({
        salon: selectedSalonId || '',
        team_member: selectedTeamMemberId || '',
        day_of_week: '',
        start_time: '',
        end_time: '',
        schedule_times: defaultScheduleTimes
    });
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    
    // État pour les notifications
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success' // 'success', 'error', 'warning', 'info'
    });
    
    // États pour la gestion des pauses
    const [isAddBreakDialogOpen, setIsAddBreakDialogOpen] = useState(false);
    const [selectedBreakData, setSelectedBreakData] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    
    // Fonction pour afficher une notification
    const showNotification = (message, severity = 'success') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };
    
    // Fonction pour fermer la notification
    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification(prev => ({
            ...prev,
            open: false
        }));
    };
    
    // État pour les interrupteurs de pause par jour
const [breakSwitches, setBreakSwitches] = useState({});

// État pour les données de pause par jour - structure modifiée pour supporter plusieurs pauses
const [breakData, setBreakData] = useState({});

// État pour stocker les pauses confirmées par jour
const [confirmedBreaks, setConfirmedBreaks] = useState({});

// État pour la confirmation de suppression de pause
const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    breakToDelete: null,
    dayName: null
});

// État pour suivre si la suppression est en cours
const [isDeleting, setIsDeleting] = useState(false);
    
    // Get current user profile
    const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
    
    // Fetch salons - only when no selectedSalonId is provided
    const { data: salonsData, isLoading: salonsLoading } = useSalonsListQuery(
        userProfile && !selectedSalonId ? { owner: userProfile.id } : undefined,
        { skip: !userProfile || !!selectedSalonId }
    );
    
    // Fetch selected salon details to get opening hours
    const { data: selectedSalonData, isLoading: salonDetailsLoading } = useSalonsRetrieveQuery(
        formData.salon ? { salonId: formData.salon } : undefined,
        { skip: !formData.salon }
    );
    
    // Fetch team members when salon is selected
    const { data: teamMembersData, isLoading: teamMembersLoading } = useTeamsTeamMembersSalonMembersRetrieveQuery(
        formData.salon ? { salon: formData.salon } : undefined,
        { skip: !formData.salon }
    );
    
    // RTK hooks for creating schedules and breaks
    const [createSchedule, { isLoading: isCreating }] = useSchedulesSchedulesCreateMutation();
    const [createBreak, { isLoading: isCreatingBreak }] = useSchedulesBreaksCreateMutation();
    
    // Days of the week with translated names
    const weekdays = [
        { id: 'lun', name: t('pro_team_member.add_schedule.weekdays.mon'), fullName: t('pro_team_member.add_schedule.weekdays_full.monday'), icon: '📅' },
        { id: 'mar', name: t('pro_team_member.add_schedule.weekdays.tue'), fullName: t('pro_team_member.add_schedule.weekdays_full.tuesday'), icon: '📅' },
        { id: 'mer', name: t('pro_team_member.add_schedule.weekdays.wed'), fullName: t('pro_team_member.add_schedule.weekdays_full.wednesday'), icon: '📅' },
        { id: 'jeu', name: t('pro_team_member.add_schedule.weekdays.thu'), fullName: t('pro_team_member.add_schedule.weekdays_full.thursday'), icon: '📅' },
        { id: 'ven', name: t('pro_team_member.add_schedule.weekdays.fri'), fullName: t('pro_team_member.add_schedule.weekdays_full.friday'), icon: '📅' },
        { id: 'sam', name: t('pro_team_member.add_schedule.weekdays.sat'), fullName: t('pro_team_member.add_schedule.weekdays_full.saturday'), icon: '📅' },
        { id: 'dim', name: t('pro_team_member.add_schedule.weekdays.sun'), fullName: t('pro_team_member.add_schedule.weekdays_full.sunday'), icon: '📅' }
    ];
    
    // Selected days for schedule
    const [selectedDays, setSelectedDays] = useState(['lun', 'mar', 'mer', 'jeu', 'ven']);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };
    
    const handleDayChange = (event, newDays) => {
        // Disallow selecting days where the salon is closed
        const salonOpenDayIds = selectedSalonData ? getOpenDays(selectedSalonData) : weekdays.map(d => d.id);
        const filteredDays = (newDays || []).filter(dayId => salonOpenDayIds.includes(dayId));

        // Prevent deselecting all days
        if (filteredDays && filteredDays.length) {
            setSelectedDays(filteredDays);
            
            // Update the day_of_week in formData - use the first selected day as the primary
            const primaryDayId = filteredDays[0];
            const fullDayName = weekdays.find(day => day.id === primaryDayId)?.fullName || '';
            
            setFormData(prev => ({
                ...prev,
                day_of_week: fullDayName
            }));
            
            // Clear error when field is edited
            if (errors.day_of_week) {
                setErrors(prev => ({
                    ...prev,
                    day_of_week: ''
                }));
            }
        }
    };
    
    const handleTimeChange = (dayName, timeType, value) => {
        // Format the time value if it's a dayjs object
        const timeValue = value ? 
            (typeof value === 'string' ? value : value.format('HH:mm')) : 
            '';
            
        setFormData(prev => ({
            ...prev,
            schedule_times: {
                ...prev.schedule_times,
                [dayName]: {
                    ...prev.schedule_times[dayName],
                    [timeType]: timeValue
                }
            }
        }));
        
        // Clear error when field is edited
        if (errors[`${dayName}_${timeType}`]) {
            setErrors(prev => ({
                ...prev,
                [`${dayName}_${timeType}`]: ''
            }));
        }
        
        // Also update the primary day's time if this is the first selected day
        if (selectedDays.length > 0 && weekdays.find(day => day.id === selectedDays[0])?.fullName === dayName) {
            setFormData(prev => ({
                ...prev,
                [timeType]: timeValue
            }));
        }
        
        // Close the time picker popover
        setTimePickerAnchor(null);
        setActiveTimePicker(null);
    };

    // Handle direct time input changes (typing) with auto-formatting
    const handleTimeInputChange = (dayName, timeType, value) => {
        if (!value || value === 'fermé') {
            setFormData(prev => ({
                ...prev,
                schedule_times: {
                    ...prev.schedule_times,
                    [dayName]: {
                        ...prev.schedule_times[dayName],
                        [timeType]: 'fermé'
                    }
                }
            }));
            return;
        }

        // Remove non-numeric characters except colon
        let cleanValue = value.replace(/[^\d:]/g, '');
        
        // Auto-format as user types
        if (cleanValue.length === 1 && parseInt(cleanValue) > 2) {
            cleanValue = '0' + cleanValue + ':';
        } else if (cleanValue.length === 2 && !cleanValue.includes(':')) {
            const hours = parseInt(cleanValue);
            if (hours > 23) {
                cleanValue = '23:';
            } else {
                cleanValue = cleanValue + ':';
            }
        } else if (cleanValue.length === 3 && cleanValue.endsWith(':')) {
            // Keep as is - user typed HH:
        } else if (cleanValue.length === 4 && cleanValue.includes(':')) {
            const [hours, minutes] = cleanValue.split(':');
            const minuteValue = parseInt(minutes);
            if (minuteValue > 5) {
                cleanValue = hours + ':0' + minutes;
            }
        } else if (cleanValue.length === 5) {
            const [hours, minutes] = cleanValue.split(':');
            const hourValue = parseInt(hours);
            const minuteValue = parseInt(minutes);
            
            if (hourValue > 23) {
                cleanValue = '23:' + minutes;
            }
            if (minuteValue > 59) {
                cleanValue = hours + ':59';
            }
        }

        // Validate final format
        const timeRegex = /^([01]?[0-9]|2[0-3]):?([0-5]?[0-9]?)$/;
        if (!timeRegex.test(cleanValue)) {
            return;
        }

        setFormData(prev => ({
            ...prev,
            schedule_times: {
                ...prev.schedule_times,
                [dayName]: {
                    ...prev.schedule_times[dayName],
                    [timeType]: cleanValue
                }
            }
        }));
        
        // Clear error when field is edited
        if (errors[`${dayName}_${timeType}`]) {
            setErrors(prev => ({
                ...prev,
                [`${dayName}_${timeType}`]: ''
            }));
        }
        
        // Also update the primary day's time if this is the first selected day
        if (selectedDays.length > 0 && weekdays.find(day => day.id === selectedDays[0])?.fullName === dayName) {
            setFormData(prev => ({
                ...prev,
                [timeType]: cleanValue
            }));
        }
    };

    // Handle time picker open
    const handleTimePickerOpen = (event, dayName, timeType) => {
        setTimePickerAnchor(event.currentTarget);
        setActiveTimePicker({ dayName, timeType });
    };

    // Handle time picker close
    const handleTimePickerClose = () => {
        setTimePickerAnchor(null);
        setActiveTimePicker(null);
    };
    
    // Copy times from the first selected day to all other selected days
    const handleCopyToAll = () => {
        if (selectedDays.length <= 1) return;
        
        const firstDayId = selectedDays[0];
        const firstDayName = weekdays.find(day => day.id === firstDayId)?.fullName;
        
        if (!firstDayName) return;
        
        const sourceTimes = formData.schedule_times?.[firstDayName] || { start_time: '', end_time: '' };
        
        // Create a new schedule_times object with the copied times
        const newScheduleTimes = { ...formData.schedule_times };
        
        selectedDays.forEach(dayId => {
            const dayName = weekdays.find(day => day.id === dayId)?.fullName;
            if (dayName && dayName !== firstDayName) {
                newScheduleTimes[dayName] = { ...sourceTimes };
            }
        });
        
        setFormData(prev => ({
            ...prev,
            schedule_times: newScheduleTimes
        }));
    };

    // Copy breaks from a specific day to all other selected days
    const handleCopyBreaksToAll = (sourceDayName) => {
        if (selectedDays.length <= 1) return;
        
        if (!sourceDayName) return;
        
        // Get source breaks and break switch state
        const sourceBreaks = confirmedBreaks[sourceDayName] || [];
        const sourceBreakSwitch = breakSwitches[sourceDayName] || false;
        
        if (!sourceBreakSwitch || sourceBreaks.length === 0) {
            setNotification({
                open: true,
                message: t('pro_team_member.add_schedule.no_breaks_to_copy'),
                severity: 'warning'
            });
            return;
        }
        
        // Copy breaks to all other selected days
        const newConfirmedBreaks = { ...confirmedBreaks };
        const newBreakSwitches = { ...breakSwitches };
        
        selectedDays.forEach(dayId => {
            const dayName = weekdays.find(day => day.id === dayId)?.fullName;
            if (dayName && dayName !== sourceDayName) {
                // Copy breaks with new IDs and updated backend_day
                const backendDay = getBackendDayFormat(dayName);
                newConfirmedBreaks[dayName] = sourceBreaks.map(breakItem => ({
                    ...breakItem,
                    id: Date.now() + Math.random(), // Generate unique ID
                    backend_day: backendDay
                }));
                // Enable break switch for this day
                newBreakSwitches[dayName] = true;
            }
        });
        
        setConfirmedBreaks(newConfirmedBreaks);
        setBreakSwitches(newBreakSwitches);
        
        setNotification({
            open: true,
            message: t('pro_team_member.add_schedule.breaks_copied', { count: selectedDays.length - 1 }),
            severity: 'success'
        });
    };
    
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.salon && !selectedSalonId) {
            newErrors.salon = t('pro_team_member.add_schedule.select_salon');
        }
        
        if (!formData.team_member) {
            newErrors.team_member = t('pro_team_member.add_schedule.select_member');
        }
        
        // Check if at least one day is selected
        if (selectedDays.length === 0) {
            newErrors.day_of_week = t('pro_team_member.add_schedule.select_days');
        }
        
        // Check if times are set for selected days and if they are within salon opening hours
        let hasTimeError = false;
        Object.entries(formData.schedule_times).forEach(([dayName, times]) => {
            const dayId = weekdays.find(d => d.fullName === dayName)?.id;
            if (dayId && selectedDays.includes(dayId)) {
                if (!times.start_time) {
                    newErrors[`${dayName}_start_time`] = t('pro_team_member.add_schedule.start_time_required');
                    hasTimeError = true;
                }
                if (!times.end_time) {
                    newErrors[`${dayName}_end_time`] = t('pro_team_member.add_schedule.end_time_required');
                    hasTimeError = true;
                }
                
                // Validate against salon opening hours
                if (selectedSalonData && selectedSalonData.opening_hours && times.start_time && times.end_time) {
                    const dayLowerCase = dayName.toLowerCase();
                    const salonHours = selectedSalonData.opening_hours[dayLowerCase];
                    
                    if (salonHours) {
                        // Check if salon is open on this day
                        if (salonHours.ouverture === 'fermé' || salonHours.fermeture === 'fermé') {
                            newErrors[`${dayName}_salon_closed`] = t('pro_team_member.add_schedule.salon_closed');
                            hasTimeError = true;
                        } else {
                            if (times.start_time === times.end_time) {
                                newErrors[`${dayName}_end_time`] = t('pro_team_member.add_schedule.end_time_must_differ');
                                hasTimeError = true;
                            } else if (!isWithinSalonHours(times.start_time, times.end_time, salonHours.ouverture, salonHours.fermeture)) {
                                newErrors[`${dayName}_start_time`] = t('pro_team_member.add_schedule.times_outside_salon_hours', { start: salonHours.ouverture, end: salonHours.fermeture });
                                hasTimeError = true;
                            }
                        }
                    }
                }
            }
        });
        
        if (hasTimeError) {
            newErrors.schedule_times = t('pro_team_member.add_schedule.set_times_for_all_days');
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    // Helper function to convert time string (HH:MM or HH:MM:SS) to minutes
    const timeToMinutes = (timeStr) => {
        if (!timeStr || timeStr === 'fermé') return 0;
        const [hours, minutes] = timeStr.split(':');
        return parseInt(hours) * 60 + parseInt(minutes || 0);
    };

    const normalizeInterval = (startTime, endTime, { allowEqualAsFullDay = false } = {}) => {
        const start = timeToMinutes(startTime);
        let end = timeToMinutes(endTime);
        if (end < start || (allowEqualAsFullDay && end === start)) {
            end += 24 * 60;
        }
        return { start, end };
    };

    const isWithinSalonHours = (employeeStart, employeeEnd, salonOpen, salonClose) => {
        const salonInterval = normalizeInterval(salonOpen, salonClose, { allowEqualAsFullDay: true });
        const employeeInterval = normalizeInterval(employeeStart, employeeEnd);
        return (
            employeeInterval.start >= salonInterval.start
            && employeeInterval.end <= salonInterval.end
            && employeeInterval.end > employeeInterval.start
        );
    };
    
    const formatTimeForApi = (time) => {
        if (!time) return '';
        // Make sure time includes seconds for the API
        if (time.includes(':')) {
            // If time already has a format like HH:MM
            if (time.split(':').length === 2) {
                return `${time}:00`; // Add seconds
            }
        }
        return time;
    };
    
    const handleSubmit = async () => {
        if (!validateForm()) return;
        
        try {
            // Create schedules for each selected day
            const createdSchedules = [];
            
            for (const dayId of selectedDays) {
                const dayName = weekdays.find(day => day.id === dayId)?.fullName;
                if (dayName) {
                    const daySchedule = formData.schedule_times?.[dayName] || { start_time: '', end_time: '' };
                    
                    // Only create schedules for days with valid times
                    if (daySchedule.start_time && daySchedule.end_time) {
                        // Format the data for the API
                        const createData = {
                            scheduleRequest: {
                                salon: Number(formData.salon),
                                team_member: Number(formData.team_member),
                                // Use utility function for proper day name conversion
                                day_of_week: getBackendDayFormat(dayName),
                                start_time: formatTimeForApi(daySchedule.start_time),
                                end_time: formatTimeForApi(daySchedule.end_time)
                            }
                        };
                        
                        // We'll create breaks separately after the schedule is created
                        // Remove break_requests from schedule creation since it's not supported
                        
                        const result = await createSchedule(createData).unwrap();
                        createdSchedules.push(result);
                        
                        // Now create breaks for this schedule if there are any
                        if (breakSwitches[dayName] && confirmedBreaks[dayName] && confirmedBreaks[dayName].length > 0) {
                            
                            // Create each break separately using the break creation endpoint
                            for (const breakItem of confirmedBreaks[dayName]) {
                                try {
                                    const breakRequest = {
                                        team_member: Number(formData.team_member),
                                        salon: Number(formData.salon),
                                        day_of_week: breakItem.backend_day,
                                        start_time: formatTimeForApi(breakItem.start_time),
                                        end_time: formatTimeForApi(breakItem.end_time),
                                        description: breakItem.description || 'Pause'
                                    };
                                    
                                    await createBreak({ breakRequest }).unwrap();
                                } catch (breakError) {
                                    console.error('Failed to create break:', breakError);
                                    // Continue with other breaks even if one fails
                                }
                            }
                        }
                    }
                }
            }
            
            // Reset form and close dialog on success
            setFormData({
                salon: '',
                team_member: '',
                day_of_week: '',
                start_time: '',
                end_time: '',
                schedule_times: defaultScheduleTimes
            });
            setSelectedDays(['lun', 'mar', 'mer', 'jeu', 'ven']);
            
            // Afficher une notification de succès
            showNotification(t('pro_team_member.add_schedule.schedule_created_success'), 'success');
            
            // Call success callback and close dialog
            if (onSuccess) {
                onSuccess(createdSchedules);
            }
            onClose();
        } catch (error) {
            console.error('Failed to create schedule:', error);
            
            // Construire un message d'erreur pour la notification
            let errorMessage = t('pro_team_member.add_schedule.error_creating_schedule');
            
            // Handle API validation errors
            if (error.data && error.data.errors) {
                setErrors(error.data.errors);
                errorMessage = Object.values(error.data.errors).flat().join(', ');
            } else if (error.data) {
                // Handle non-field errors or other error formats
                const apiErrors = {};
                let errorDetails = [];
                
                if (error.data.salon) {
                    apiErrors.salon = error.data.salon.join(' ');
                    errorDetails.push(`${t('pro_team_member.add_schedule.salon_error')}: ${error.data.salon.join(' ')}`);
                }
                if (error.data.team_member) {
                    apiErrors.team_member = error.data.team_member.join(' ');
                    errorDetails.push(`${t('pro_team_member.add_schedule.member_error')}: ${error.data.team_member.join(' ')}`);
                }
                if (error.data.day_of_week) {
                    apiErrors.day_of_week = error.data.day_of_week.join(' ');
                    errorDetails.push(`${t('pro_team_member.add_schedule.day_error')}: ${error.data.day_of_week.join(' ')}`);
                }
                if (error.data.start_time) {
                    apiErrors.start_time = error.data.start_time.join(' ');
                    errorDetails.push(`${t('pro_team_member.add_schedule.start_error')}: ${error.data.start_time.join(' ')}`);
                }
                if (error.data.end_time) {
                    apiErrors.end_time = error.data.end_time.join(' ');
                    errorDetails.push(`${t('pro_team_member.add_schedule.end_error')}: ${error.data.end_time.join(' ')}`);
                }
                if (error.data.non_field_errors) {
                    // Show non-field errors in a visible field
                    apiErrors.day_of_week = error.data.non_field_errors.join(' ');
                    errorDetails.push(error.data.non_field_errors.join(' '));
                }
                
                // Si nous avons des détails d'erreur, les utiliser pour la notification
                if (errorDetails.length > 0) {
                    errorMessage = errorDetails.join(', ');
                }
                
                setErrors(apiErrors);
            }
            
            // Afficher la notification d'erreur
            showNotification(errorMessage, 'error');
        }
    };
    
    // Set selected salon ID when it's provided as prop
    useEffect(() => {
        if (selectedSalonId) {
            setFormData(prev => ({
                ...prev,
                salon: selectedSalonId
            }));
        }
    }, [selectedSalonId]);

    // Set selected team member ID when it's provided as prop
    useEffect(() => {
        if (selectedTeamMemberId) {
            setFormData(prev => ({
                ...prev,
                team_member: selectedTeamMemberId
            }));
        }
    }, [selectedTeamMemberId]);
    
    // Update schedule times and selected days when salon data loads
    useEffect(() => {
        if (selectedSalonData && selectedSalonData.opening_hours) {
            const salonScheduleTimes = getDefaultScheduleTimes(selectedSalonData);
            const openDays = getOpenDays(selectedSalonData);
            
            setFormData(prev => ({
                ...prev,
                schedule_times: salonScheduleTimes
            }));
            
            // Auto-select days where salon is open
            setSelectedDays(openDays);
        }
    }, [selectedSalonData]);

    // Reset form when dialog is closed
    useEffect(() => {
        if (!open && dialogClosed) {
            // Reset form only when dialog was previously open and is now closed
            setFormData({
                salon: selectedSalonId || '',
                team_member: '',
                day_of_week: '',
                start_time: '',
                end_time: '',
                schedule_times: defaultScheduleTimes
            });
            setSelectedDays(['lun', 'mar', 'mer', 'jeu', 'ven']);
            setErrors({});
            setDialogClosed(false);
        } else if (open) {
            setDialogClosed(true);
        }
    }, [open, selectedSalonId, dialogClosed]);

    // Form content to be displayed in the dialog
    const formContent = (
        <>
            {/* Section principale */}
            <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="stretch">
                    {/* Salon Selection */}
                    <Grid item xs={12} md={6}>
                        {selectedSalonId ? (
                            // Show selected salon as read-only field
                            <TextField
                                fullWidth
                                label={t('pro_team_member.add_schedule.salon')}
                                value={selectedSalonData?.name || t('pro_team_member.add_schedule.loading_salon')}
                                disabled={true}
                                variant="outlined"
                                sx={{ 
                                    minHeight: 80, 
                                    minWidth: '250px',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'rgba(240, 240, 240, 0.5)',
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        ) : (
                            // Show salon dropdown for selection
                            <FormControl 
                                fullWidth 
                                error={!!errors.salon}
                                disabled={isCreating || salonsLoading || profileLoading}
                                variant="outlined"
                                sx={{ minHeight: 80, minWidth: '250px' }}
                            >
                                <InputLabel>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <BusinessIcon sx={{ fontSize: 18, mr: 1 }} />
                                        Salon
                                    </Box>
                                </InputLabel>
                                <Select
                                    name="salon"
                                    value={formData.salon}
                                    onChange={handleChange}
                                    label={t('pro_team_member.add_schedule.salon')}
                                    sx={{
                                        minHeight: 56,
                                        fontSize: '1rem',
                                        width: '100%',
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            minHeight: 56,
                                        },
                                        '& .MuiSelect-select': {
                                            minHeight: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '1rem',
                                            padding: '14px 14px',
                                        },
                                        '& .MuiMenu-paper': {
                                            minWidth: '250px',
                                            maxWidth: '100%'
                                        }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                minWidth: '250px',
                                                maxWidth: '100%'
                                            }
                                        }
                                    }}
                                    displayEmpty
                                >
                                    {salonsLoading ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            {t('pro_team_member.add_member.loading_salons')}
                                        </MenuItem>
                                    ) : salonsData && salonsData.length > 0 ? (
                                        salonsData.map(salon => (
                                            <MenuItem key={salon.salon_id} value={salon.salon_id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <BusinessIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} />
                                                    {salon.name}
                                                </Box>
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>{t('pro_team_member.add_member.no_salons_available')}</MenuItem>
                                    )}
                                </Select>
                                {errors.salon && <FormHelperText>{errors.salon}</FormHelperText>}
                            </FormControl>
                        )}
                    </Grid>

                    {/* Team Member Selection */}
                    <Grid item xs={12} md={6}>
                        <FormControl 
                            fullWidth 
                            error={!!errors.team_member}
                            disabled={isCreating || teamMembersLoading || !formData.salon}
                            variant="outlined"
                            sx={{ minHeight: 80, minWidth: '250px' }}
                        >
                            <InputLabel>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 18, mr: 1 }} />
                                    {t('pro_team_member.add_schedule.team_member')}
                                </Box>
                            </InputLabel>
                            <Select
                                name="team_member"
                                value={formData.team_member}
                                onChange={handleChange}
                                label={t('pro_team_member.add_schedule.team_member')}
                                sx={{
                                    minHeight: 56,
                                    fontSize: '1rem',
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        minHeight: 56,
                                    },
                                    '& .MuiSelect-select': {
                                        minHeight: 'auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '1rem',
                                        padding: '14px 14px',
                                    },
                                    '& .MuiMenu-paper': {
                                        minWidth: '250px',
                                        maxWidth: '100%'
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            minWidth: '250px',
                                            maxWidth: '100%'
                                        }
                                    }
                                }}
                                displayEmpty
                            >
                                {/* Creation mode: Show specific member name, Edit mode: Show all members */}
                                {!formData.salon ? (
                                    <MenuItem disabled>{t('pro_team_member.add_schedule.select_salon_first')}</MenuItem>
                                ) : teamMembersLoading ? (
                                    <MenuItem disabled>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        {t('pro_team_member.add_schedule.loading_members')}
                                    </MenuItem>
                                ) : isCreationMode ? (
                                    // Creation mode: Show only the selected member (disabled)
                                    teamMembersData && teamMembersData.length > 0 ? (
                                        teamMembersData
                                            .filter(member => member.team_member_id === selectedTeamMemberId)
                                            .map(member => (
                                                <MenuItem key={member.team_member_id} value={member.team_member_id} disabled>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <PersonIcon sx={{ fontSize: 18, mr: 1, color: 'success.main' }} />
                                                        {member.user_details?.full_name || t('pro_team_member.add_schedule.unnamed_member')}
                                                        <Chip 
                                                            label={t('pro_team_member.add_schedule.selected')}
                                                            size="small" 
                                                            color="success" 
                                                            sx={{ ml: 1, fontSize: '0.7rem' }}
                                                        />
                                                    </Box>
                                                </MenuItem>
                                            ))
                                    ) : (
                                        <MenuItem disabled>{t('pro_team_member.add_schedule.member_not_found')}</MenuItem>
                                    )
                                ) : (
                                    // Edit mode: Show all salon members
                                    teamMembersData && teamMembersData.length > 0 ? (
                                        teamMembersData.map(member => (
                                            <MenuItem key={member.team_member_id} value={member.team_member_id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PersonIcon sx={{ fontSize: 18, mr: 1, color: 'success.main' }} />
                                                    {member.user_details?.full_name || t('pro_team_member.add_schedule.unnamed_member')}
                                                </Box>
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>{t('pro_team_member.add_schedule.no_members_available')}</MenuItem>
                                    )
                                )}
                            </Select>
                            {errors.team_member && <FormHelperText>{errors.team_member}</FormHelperText>}
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: { xs: 2, md: 3 } }} />
            
            {/* Day Selection Section */}
            <Box sx={{ mb: { xs: 2, md: 4 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 }, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    <CalendarMonthIcon sx={{ mr: 1, color: 'primary.main' }} />
                    {t('pro_team_member.add_schedule.work_days')}
                </Typography>
                
                <Card variant="outlined" sx={{ mb: { xs: 1, md: 2 }, backgroundColor: `rgba(${theme.colors.primary[500].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.04)` }}>
                    <CardContent sx={{ py: { xs: 1, md: 2 } }}>
                        <ToggleButtonGroup 
                            value={selectedDays} 
                            onChange={handleDayChange}
                            aria-label={t('pro_team_member.add_schedule.work_days')}
                            size="medium"
                            color="primary"
                            disabled={isCreating}
                            sx={{ 
                                display: 'grid',
                                gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(7, 1fr)' },
                                gap: { xs: 0.5, md: 1 },
                                width: '100%',
                                '& .MuiToggleButton-root': {
                                    border: `1px solid ${theme.colors.primary[300]}`,
                                    borderRadius: 2,
                                    px: { xs: 1, md: 1.5 },
                                    py: { xs: 0.5, md: 1 },
                                    fontWeight: 'medium',
                                    '&.Mui-disabled': {
                                        backgroundColor: theme.colors.grey[100],
                                        color: theme.colors.text.secondary,
                                        borderColor: theme.colors.grey[200],
                                        cursor: 'not-allowed',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: theme.colors.primary[500],
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: theme.colors.primary[600],
                                        }
                                    }
                                }
                            }}
                        >
                            {weekdays.map(day => (
                                <Tooltip
                                    key={day.id}
                                    title={(() => {
                                        if (!selectedSalonData) return '';
                                        return isSalonOpenOnDay(selectedSalonData, day.id) ? '' : t('pro_team_member.add_schedule.salon_closed');
                                    })()}
                                    arrow
                                    disableHoverListener={(() => {
                                        if (!selectedSalonData) return true;
                                        return isSalonOpenOnDay(selectedSalonData, day.id);
                                    })()}
                                >
                                    <span>
                                        <ToggleButton 
                                            value={day.id}
                                            disabled={(() => {
                                                if (!selectedSalonData) return false;
                                                return !isSalonOpenOnDay(selectedSalonData, day.id);
                                            })()}
                                            sx={{ minWidth: 0, width: '100%' }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                                    {day.name}
                                                </Typography>
                                            </Box>
                                        </ToggleButton>
                                    </span>
                                </Tooltip>
                            ))}
                        </ToggleButtonGroup>
                        {errors.day_of_week && (
                            <FormHelperText error sx={{ mt: 1 }}>{errors.day_of_week}</FormHelperText>
                        )}
                    </CardContent>
                </Card>
                
                {selectedDays.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('pro_team_member.add_schedule.selected_days')}:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: '100%' }}>
                            {selectedDays.map(dayId => {
                                const day = weekdays.find(d => d.id === dayId);
                                return (
                                    <Chip 
                                        key={dayId}
                                        label={day?.fullName}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: { xs: 2, md: 3 } }} />
            
            {/* Time Configuration Section */}
            <Box sx={{ mb: { xs: 1, md: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                        <AccessTimeIcon sx={{ mr: 1, color: 'success.main' }} />
                        {t('pro_team_member.add_schedule.work_hours')}
                    </Typography>
                    {selectedDays.length > 1 && (
                        <Tooltip title={t('pro_team_member.add_schedule.copy_to_all_tooltip')}>
                            <Button 
                                size="small" 
                                variant="outlined" 
                                color="primary" 
                                onClick={handleCopyToAll}
                                disabled={isCreating}
                                startIcon={<ContentCopyIcon />}
                                sx={{ 
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 'medium'
                                }}
                            >
                                {t('pro_team_member.add_schedule.copy_to_all')}
                            </Button>
                        </Tooltip>
                    )}
                </Box>
                
                {weekdays.map(day => {
                    const dayName = day.fullName;
                    const isSelected = selectedDays.includes(day.id);
                    const times = formData.schedule_times?.[dayName] || { start_time: '', end_time: '' };
                    
                    if (!isSelected) return null;

                    return (
                        <Card key={day.id} variant="outlined" sx={{ mb: { xs: 2, md: 3 }, backgroundColor: `rgba(${theme.colors.primary[500].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.04)` }}>
                            <CardContent sx={{ py: { xs: 1.5, md: 2 }, px: { xs: 1.5, md: 2 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                        {day.icon} {dayName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ mr: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                            {t('pro_team_member.add_schedule.break')}
                                        </Typography>
                                        <Switch
                                            checked={!!breakSwitches[dayName]}
                                            onChange={(e) => {
                                                setBreakSwitches(prev => ({
                                                    ...prev,
                                                    [dayName]: e.target.checked
                                                }));
                                                
                                                // Initialize break data for this day if switch is turned on
                                                if (e.target.checked && !breakData[dayName]) {
                                                    setBreakData(prev => ({
                                                        ...prev,
                                                        [dayName]: {
                                                            start_time: '',
                                                            end_time: '',
                                                            description: t('pro_team_member.add_schedule.break')
                                                        }
                                                    }));
                                                }
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: theme.colors.secondary[300],
                                                    '&:hover': {
                                                        backgroundColor: `rgba(${theme.colors.secondary[300].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1)`,
                                                    },
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: theme.colors.secondary[300],
                                                },
                                            }}
                                            size="small"
                                            disabled={isCreating}
                                        />
                                        <Tooltip title={t('pro_team_member.add_schedule.break_tooltip')}>
                                            <CoffeeIcon sx={{ ml: 0.5, color: breakSwitches[dayName] ? theme.colors.secondary[300] : 'rgba(0, 0, 0, 0.26)' }} />
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <Grid container spacing={{ xs: 1, md: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                                label={t('pro_team_member.add_schedule.start_time')}
                                                value={times.start_time !== 'fermé' ? times.start_time || '' : ''}
                                                onChange={(e) => handleTimeInputChange(dayName, 'start_time', e.target.value)}
                                                disabled={isCreating || !isSelected}
                                                size="small"
                                                fullWidth
                                                placeholder="09:00"
                                                error={!!errors[`${dayName}_start_time`]}
                                                helperText={errors[`${dayName}_start_time`]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <AccessTimeIcon color="action" />
                                                        </InputAdornment>
                                                    )
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: '#fff',
                                                        borderRadius: 1.5,
                                                        '& fieldset': {
                                                            borderColor: '#e0e0e0',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#ccc',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme.colors.primary[500],
                                                        }
                                                    }
                                                }}
                                            />
                                            <IconButton
                                                onClick={(e) => handleTimePickerOpen(e, dayName, 'start_time')}
                                                disabled={isCreating || !isSelected}
                                                size="small"
                                                sx={{
                                                    color: theme.colors.primary[500],
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0,0,0,0.04)'
                                                    }
                                                }}
                                            >
                                                <AccessTimeIcon />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                                label={t('pro_team_member.add_schedule.end_time')}
                                                value={times.end_time !== 'fermé' ? times.end_time || '' : ''}
                                                onChange={(e) => handleTimeInputChange(dayName, 'end_time', e.target.value)}
                                                disabled={isCreating || !isSelected}
                                                size="small"
                                                fullWidth
                                                placeholder="18:00"
                                                error={!!errors[`${dayName}_end_time`]}
                                                helperText={errors[`${dayName}_end_time`]}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <AccessTimeIcon color="action" />
                                                        </InputAdornment>
                                                    )
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: '#fff',
                                                        borderRadius: 1.5,
                                                        '& fieldset': {
                                                            borderColor: '#e0e0e0',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#ccc',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme.colors.primary[500],
                                                        }
                                                    }
                                                }}
                                            />
                                            <IconButton
                                                onClick={(e) => handleTimePickerOpen(e, dayName, 'end_time')}
                                                disabled={isCreating || !isSelected}
                                                size="small"
                                                sx={{
                                                    color: theme.colors.primary[500],
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0,0,0,0.04)'
                                                    }
                                                }}
                                            >
                                                <AccessTimeIcon />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                                
                                {/* Break time inputs - shown only when the switch is on */}
                                {breakSwitches[dayName] && (
                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                        <Paper elevation={0} sx={{ 
                                            p: 2, 
                                            backgroundColor: theme.colors.secondary && theme.colors.secondary[300] ? 
                                                `rgba(${theme.colors.secondary[300].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1)` : 
                                                'rgba(156, 39, 176, 0.05)', 
                                            borderRadius: 2, 
                                            border: `1px solid ${theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0'}` 
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CoffeeIcon sx={{ mr: 1, fontSize: 18, color: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0' }} />
                                                    {t('pro_team_member.add_schedule.add_break')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleCopyBreaksToAll(dayName)}
                                                        disabled={isCreating || selectedDays.length <= 1 || !confirmedBreaks[dayName] || confirmedBreaks[dayName].length === 0}
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderColor: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0',
                                                            color: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0',
                                                            '&:hover': {
                                                                borderColor: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#7b1fa2',
                                                                backgroundColor: theme.colors.secondary && theme.colors.secondary[50] ? theme.colors.secondary[50] : 'rgba(156, 39, 176, 0.04)',
                                                            },
                                                            '&.Mui-disabled': {
                                                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                                                color: 'rgba(0, 0, 0, 0.26)',
                                                            }
                                                        }}
                                                    >
                                                        {t('pro_team_member.add_schedule.copy_breaks_to_all')}
                                                    </Button>
                                                    <Chip 
                                                        label={t('pro_team_member.add_schedule.breaks_count', { count: confirmedBreaks[dayName]?.length || 0 })} 
                                                        size="small" 
                                                        sx={{ 
                                                            fontWeight: 'medium',
                                                            borderColor: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0',
                                                            color: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0'
                                                        }}
                                                        variant="outlined" 
                                                    />
                                                </Box>
                                            </Box>
                                            
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            label={t('pro_team_member.add_schedule.break_start')}
                                                            value={breakData[dayName]?.start_time || ''}
                                                            onChange={(e) => {
                                                                // Use the enhanced time input handler with auto-formatting
                                                                const value = e.target.value;
                                                                if (!value || value === 'fermé') {
                                                                    setBreakData(prev => ({
                                                                        ...prev,
                                                                        [dayName]: {
                                                                            ...prev[dayName],
                                                                            start_time: ''
                                                                        }
                                                                    }));
                                                                    return;
                                                                }

                                                                // Remove non-numeric characters except colon
                                                                let cleanValue = value.replace(/[^\d:]/g, '');
                                                                
                                                                // Auto-format as user types
                                                                if (cleanValue.length === 1 && parseInt(cleanValue) > 2) {
                                                                    cleanValue = '0' + cleanValue + ':';
                                                                } else if (cleanValue.length === 2 && !cleanValue.includes(':')) {
                                                                    const hours = parseInt(cleanValue);
                                                                    if (hours > 23) {
                                                                        cleanValue = '23:';
                                                                    } else {
                                                                        cleanValue = cleanValue + ':';
                                                                    }
                                                                } else if (cleanValue.length === 3 && cleanValue.endsWith(':')) {
                                                                    // Keep as is - user typed HH:
                                                                } else if (cleanValue.length === 4 && cleanValue.includes(':')) {
                                                                    const [hours, minutes] = cleanValue.split(':');
                                                                    const minuteValue = parseInt(minutes);
                                                                    if (minuteValue > 5) {
                                                                        cleanValue = hours + ':0' + minutes;
                                                                    }
                                                                } else if (cleanValue.length === 5) {
                                                                    const [hours, minutes] = cleanValue.split(':');
                                                                    const hourValue = parseInt(hours);
                                                                    const minuteValue = parseInt(minutes);
                                                                    
                                                                    if (hourValue > 23) {
                                                                        cleanValue = '23:' + minutes;
                                                                    }
                                                                    if (minuteValue > 59) {
                                                                        cleanValue = hours + ':59';
                                                                    }
                                                                }

                                                                // Validate final format
                                                                const timeRegex = /^([01]?[0-9]|2[0-3]):?([0-5]?[0-9]?)$/;
                                                                if (!timeRegex.test(cleanValue)) {
                                                                    return;
                                                                }

                                                                setBreakData(prev => ({
                                                                    ...prev,
                                                                    [dayName]: {
                                                                        ...prev[dayName],
                                                                        start_time: cleanValue
                                                                    }
                                                                }));
                                                            }}
                                                            disabled={isCreating}
                                                            size="small"
                                                            fullWidth
                                                            placeholder="12:00"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <CoffeeIcon sx={{ color: theme.colors.secondary[300] }} />
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: '#fff',
                                                                    borderRadius: 1.5,
                                                                    '& fieldset': {
                                                                        borderColor: '#e0e0e0',
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#ccc',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0',
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <IconButton
                                                            onClick={(e) => handleTimePickerOpen(e, dayName, 'break_start')}
                                                            disabled={isCreating}
                                                            size="small"
                                                            sx={{
                                                                color: theme.colors.secondary[300],
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0,0,0,0.04)'
                                                                }
                                                            }}
                                                        >
                                                            <AccessTimeIcon />
                                                        </IconButton>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            label={t('pro_team_member.add_schedule.break_end')}
                                                            value={breakData[dayName]?.end_time || ''}
                                                            onChange={(e) => {
                                                                // Use the enhanced time input handler with auto-formatting
                                                                const value = e.target.value;
                                                                if (!value || value === 'fermé') {
                                                                    setBreakData(prev => ({
                                                                        ...prev,
                                                                        [dayName]: {
                                                                            ...prev[dayName],
                                                                            end_time: ''
                                                                        }
                                                                    }));
                                                                    return;
                                                                }

                                                                // Remove non-numeric characters except colon
                                                                let cleanValue = value.replace(/[^\d:]/g, '');
                                                                
                                                                // Auto-format as user types
                                                                if (cleanValue.length === 1 && parseInt(cleanValue) > 2) {
                                                                    cleanValue = '0' + cleanValue + ':';
                                                                } else if (cleanValue.length === 2 && !cleanValue.includes(':')) {
                                                                    const hours = parseInt(cleanValue);
                                                                    if (hours > 23) {
                                                                        cleanValue = '23:';
                                                                    } else {
                                                                        cleanValue = cleanValue + ':';
                                                                    }
                                                                } else if (cleanValue.length === 3 && cleanValue.endsWith(':')) {
                                                                    // Keep as is - user typed HH:
                                                                } else if (cleanValue.length === 4 && cleanValue.includes(':')) {
                                                                    const [hours, minutes] = cleanValue.split(':');
                                                                    const minuteValue = parseInt(minutes);
                                                                    if (minuteValue > 5) {
                                                                        cleanValue = hours + ':0' + minutes;
                                                                    }
                                                                } else if (cleanValue.length === 5) {
                                                                    const [hours, minutes] = cleanValue.split(':');
                                                                    const hourValue = parseInt(hours);
                                                                    const minuteValue = parseInt(minutes);
                                                                    
                                                                    if (hourValue > 23) {
                                                                        cleanValue = '23:' + minutes;
                                                                    }
                                                                    if (minuteValue > 59) {
                                                                        cleanValue = hours + ':59';
                                                                    }
                                                                }

                                                                // Validate final format
                                                                const timeRegex = /^([01]?[0-9]|2[0-3]):?([0-5]?[0-9]?)$/;
                                                                if (!timeRegex.test(cleanValue)) {
                                                                    return;
                                                                }

                                                                setBreakData(prev => ({
                                                                    ...prev,
                                                                    [dayName]: {
                                                                        ...prev[dayName],
                                                                        end_time: cleanValue
                                                                    }
                                                                }));
                                                            }}
                                                            disabled={isCreating}
                                                            size="small"
                                                            fullWidth
                                                            placeholder="13:00"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <CoffeeIcon sx={{ color: theme.colors.secondary[300] }} />
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: '#fff',
                                                                    borderRadius: 1.5,
                                                                    '& fieldset': {
                                                                        borderColor: '#e0e0e0',
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#ccc',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0',
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <IconButton
                                                            onClick={(e) => handleTimePickerOpen(e, dayName, 'break_end')}
                                                            disabled={isCreating}
                                                            size="small"
                                                            sx={{
                                                                color: theme.colors.secondary[300],
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0,0,0,0.04)'
                                                                }
                                                            }}
                                                        >
                                                            <AccessTimeIcon />
                                                        </IconButton>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label={t('pro_team_member.add_schedule.description')}
                                                        value={breakData[dayName]?.description || ''}
                                                        onChange={(e) => {
                                                            setBreakData(prev => ({
                                                                ...prev,
                                                                [dayName]: {
                                                                    ...prev[dayName],
                                                                    description: e.target.value
                                                                }
                                                            }));
                                                        }}
                                                        size="small"
                                                        fullWidth
                                                        disabled={isCreating}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <CoffeeIcon sx={{ color: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0' }} />
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: theme.colors.secondary && theme.colors.secondary[400] ? theme.colors.secondary[400] : '#9c27b0',
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#7b1fa2',
                                                            },
                                                            '&.Mui-disabled': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                            },
                                                            fontWeight: 'medium',
                                                            px: 2
                                                        }}
                                                        size="small"
                                                        startIcon={<CoffeeIcon />}
                                                        disabled={!breakData[dayName]?.start_time || !breakData[dayName]?.end_time}
                                                        onClick={async () => {
                                                            // Validate break times
                                                            const startTime = dayjs(breakData[dayName].start_time, 'HH:mm');
                                                            const endTime = dayjs(breakData[dayName].end_time, 'HH:mm');
                                                            
                                                            if (endTime.isBefore(startTime)) {
                                                                setNotification({
                                                                    open: true,
                                                                    message: t('pro_team_member.add_schedule.break_end_after_start_error'),
                                                                    severity: 'error'
                                                                });
                                                                return;
                                                            }
                                                            
                                                            // Prepare the break data for backend
                                                            const backendDay = getBackendDayFormat(dayName);
                                                            
                                                            // Store the break locally only - we'll create it in the backend when the schedule is created
                                                            const newBreak = {
                                                                start_time: breakData[dayName].start_time,
                                                                end_time: breakData[dayName].end_time,
                                                                description: breakData[dayName].description || t('pro_team_member.add_schedule.break'),
                                                                id: Date.now(), // Use a temporary local ID
                                                                backend_day: backendDay
                                                            };
                                                            
                                                            setConfirmedBreaks(prev => ({  
                                                                ...prev,
                                                                [dayName]: [...(prev[dayName] || []), newBreak]
                                                            }));
                                                            
                                                            // Reset the break form
                                                            setBreakData(prev => ({
                                                                ...prev,
                                                                [dayName]: {
                                                                    start_time: '',
                                                                    end_time: '',
                                                                    description: t('pro_team_member.add_schedule.break')
                                                                }
                                                            }));
                                                            
                                                            
                                                        }}
                                                    >
                                                        {t('pro_team_member.add_schedule.confirm_break')}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                            
                                            {/* Display confirmed breaks */}
                                            {confirmedBreaks[dayName] && confirmedBreaks[dayName].length > 0 && (
                                                <Box sx={{ mt: 3 }}>
                                                    <Divider sx={{ mb: 2 }}>
                                                        <Chip 
                                                            label={t('pro_team_member.add_schedule.confirmed_breaks')} 
                                                            size="small" 
                                                            sx={{ 
                                                                fontWeight: 'medium',
                                                                borderColor: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0',
                                                                color: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0'
                                                            }}
                                                            variant="outlined"
                                                        />
                                                    </Divider>
                                                    
                                                    {confirmedBreaks[dayName].map((breakItem, index) => (
                                                        <Paper 
                                                            key={breakItem.id} 
                                                            elevation={0}
                                                            sx={{ 
                                                                p: 1.5, 
                                                                mb: 1.5, 
                                                                backgroundColor: theme.colors.secondary && theme.colors.secondary[100] ? 
                                                                    `rgba(${theme.colors.secondary[100].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.2)` : 
                                                                    'rgba(156, 39, 176, 0.05)',
                                                                border: `1px solid ${theme.colors.secondary && theme.colors.secondary[200] ? theme.colors.secondary[200] : '#ce93d8'}`,
                                                                borderRadius: 1.5,
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <CoffeeIcon sx={{ mr: 1.5, fontSize: 16, color: theme.colors.secondary && theme.colors.secondary[400] ? theme.colors.secondary[400] : '#9c27b0' }} />
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                        {breakItem.start_time} - {breakItem.end_time}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {breakItem.description}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{ display: 'flex' }}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        color: theme.colors.primary && theme.colors.primary[500] ? theme.colors.primary[500] : '#416548',
                                                                        '&:hover': {
                                                                            backgroundColor: theme.colors.primary && theme.colors.primary[500] ? 
                                                                                `rgba(${theme.colors.primary[500].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1)` :
                                                                                'rgba(65, 101, 72, 0.1)',
                                                                        },
                                                                        mr: 0.5
                                                                    }}
                                                                    onClick={() => {
                                                                        // Set the break data to edit
                                                                        setBreakData(prev => ({
                                                                            ...prev,
                                                                            [dayName]: {
                                                                                start_time: breakItem.start_time,
                                                                                end_time: breakItem.end_time,
                                                                                description: breakItem.description
                                                                            }
                                                                        }));
                                                                        
                                                                        // Remove the break from confirmed breaks
                                                                        setConfirmedBreaks(prev => ({
                                                                            ...prev,
                                                                            [dayName]: prev[dayName].filter(b => b.id !== breakItem.id)
                                                                        }));
                                                                        
                                                                        setNotification({
                                                                            open: true,
                                                                            message: t('pro_team_member.add_schedule.edit_break'),
                                                                            severity: 'info'
                                                                        });
                                                                    }}
                                                                >
                                                                    <Tooltip title={t('pro_team_member.add_schedule.edit_break')}>
                                                                        <EditIcon sx={{ fontSize: 18 }} />
                                                                    </Tooltip>
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        color: theme.colors.error && theme.colors.error.main ? theme.colors.error.main : '#f44336',
                                                                        '&:hover': {
                                                                            backgroundColor: theme.colors.error && theme.colors.error.main ? 
                                                                                `rgba(${theme.colors.error.main.replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1)` :
                                                                                'rgba(244, 67, 54, 0.1)',
                                                                        }
                                                                    }}
                                                                    onClick={() => {
                                                                        // Show confirmation dialog instead of deleting immediately
                                                                        setDeleteConfirmDialog({
                                                                            open: true,
                                                                            breakToDelete: breakItem,
                                                                            dayName: dayName
                                                                        });
                                                                    }}
                                                                >
                                                                    <Tooltip title={t('pro_team_member.add_schedule.delete_break')}>
                                                                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                                    </Tooltip>
                                                                </IconButton>
                                                            </Box>
                                                        </Paper>
                                                    ))}
                                                </Box>
                                            )}
                                        </Paper>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
                {errors.schedule_times && (
                    <FormHelperText error>{errors.schedule_times}</FormHelperText>
                )}
            </Box>

            {/* AddBreak Dialog */}
            {isAddBreakDialogOpen && selectedBreakData && (
                <AddBreak
                    open={isAddBreakDialogOpen}
                    onClose={() => setIsAddBreakDialogOpen(false)}
                    onSuccess={() => {
                        setIsAddBreakDialogOpen(false);
                    }}
                    teamMember={selectedBreakData.teamMember}
                    salon={selectedBreakData.salon}
                    dayOfWeek={selectedBreakData.dayOfWeek.toLowerCase()}
                />
            )}
            
            {/* Notification Component */}
            <Snackbar 
                open={notification.open} 
                autoHideDuration={6000} 
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <MuiAlert 
                    elevation={6} 
                    variant="filled" 
                    onClose={handleCloseNotification} 
                    severity={notification.severity}
                >
                    {notification.message}
                </MuiAlert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <MuiDialog
                open={deleteConfirmDialog.open}
                onExternalClose={() => setDeleteConfirmDialog({ open: false, breakToDelete: null, dayName: null })}
                icon={<DeleteOutlineIcon />}
                title={t('pro_team_member.add_schedule.delete_confirm_title')}
                subtitle={t('pro_team_member.add_schedule.delete_confirm_subtitle')}
                content={
                    <Typography>
                        {t('pro_team_member.add_schedule.delete_confirm_message')}
                    </Typography>
                }
                existAction={true}
                actionTitle={isDeleting ? t('pro_team_member.add_schedule.deleting') : t('pro_team_member.add_schedule.delete')}
                actionFunction={async () => {
                    const breakItem = deleteConfirmDialog.breakToDelete;
                    const dayName = deleteConfirmDialog.dayName;
                    if (!breakItem || !dayName) return;
                    
                    setIsDeleting(true);
                    try {
                        // Remove the break from the UI
                        setConfirmedBreaks(prev => ({
                            ...prev,
                            [dayName]: prev[dayName].filter(b => b.id !== breakItem.id)
                        }));
                        
                        // Close the dialog
                        setDeleteConfirmDialog({ open: false, breakToDelete: null, dayName: null });
                    } catch (error) {
                        console.error('Failed to delete break:', error);
                        setNotification({
                            open: true,
                            message: t('pro_team_member.add_schedule.error_deleting_break'),
                            severity: 'error'
                        });
                    } finally {
                        setIsDeleting(false);
                    }
                }}
                isLoading={isDeleting}
                maxWidth="xs"
                triggerButton={<Button style={{ display: 'none' }}>Delete</Button>}
            />

        </>
    );

    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;

    return (
        <MuiDialog
            open={open}
            onExternalClose={onClose}
            icon={<CalendarMonthIcon />}
            triggerButton={triggerButton}
            title={t('pro_team_member.add_schedule.title')}
            subtitle={t('pro_team_member.add_schedule.subtitle')}
            content={
                <>
                    <Box sx={{ maxWidth: { xs: '100%', md: 1200, lg: 1400 }, mx: 'auto', px: { xs: 1, md: 2 } }}>
                        {formData.salon && selectedSalonData && (
                            <>
                                <Alert 
                                    severity="info" 
                                    sx={{ 
                                        mb: 3,
                                        backgroundColor: 'rgba(2, 136, 209, 0.08)',
                                        border: '1px solid rgba(2, 136, 209, 0.2)',
                                        borderRadius: 2,
                                        '& .MuiAlert-icon': {
                                            color: 'info.main'
                                        }
                                    }}
                                    icon={<BusinessIcon />}
                                >
                                    <Typography variant="body2" fontWeight="medium">
                                        {t('pro_team_member.add_schedule.employee_schedules_notice')}
                                    </Typography>
                                </Alert>
                                {selectedSalonData.opening_hours && (
                                    <Card 
                                        variant="outlined" 
                                        sx={{ 
                                            mb: { xs: 2, md: 2.5 }, 
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            border: '2px solid rgba(25, 118, 210, 0.1)',
                                            borderRadius: 3,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                        }}
                                    >
                                        <CardContent sx={{ p: { xs: 1, md: 1.5 } }}>
                                            <Typography 
                                                variant="h6" 
                                                gutterBottom 
                                                fontWeight="bold"
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    color: 'primary.main',
                                                    mb: 0.5,
                                                    fontSize: { xs: '0.9rem', md: '1.1rem' }
                                                }}
                                            >
                                                <BusinessIcon sx={{ mr: 0.5, fontSize: { xs: 16, md: 20 } }} />
                                                {t('pro_team_member.add_schedule.salon_opening_hours')}
                                            </Typography>
                                            <Grid container spacing={0.5} columns={{ xs: 12, md: 14 }} sx={{ width: '100%' }}>
                                                {Object.entries(selectedSalonData.opening_hours)
                                                    .sort(([dayA], [dayB]) => {
                                                        const dayOrder = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
                                                        return dayOrder.indexOf(dayA.toLowerCase()) - dayOrder.indexOf(dayB.toLowerCase());
                                                    })
                                                    .map(([day, hours]) => {
                                                    // Map French day names to translation keys
                                                    const dayKeyMap = {
                                                        'lundi': 'mon',
                                                        'mardi': 'tue',
                                                        'mercredi': 'wed',
                                                        'jeudi': 'thu',
                                                        'vendredi': 'fri',
                                                        'samedi': 'sat',
                                                        'dimanche': 'sun'
                                                    };
                                                    const dayKey = dayKeyMap[day.toLowerCase()] || 'mon';
                                                    const translatedDayName = t(`pro_team_member.add_schedule.weekdays.${dayKey}`);
                                                    const isSelectedDay = selectedDays.some(selectedDayId => {
                                                        const selectedDayName = weekdays.find(d => d.id === selectedDayId)?.fullName.toLowerCase();
                                                        return selectedDayName === day;
                                                    });
                                                    
                                                    return (
                                                        <Grid item xs={6} sm={4} md={2} key={day}>
                                                            <Paper 
                                                                elevation={0}
                                                                sx={{ 
                                                                    p: 0.3,
                                                                    backgroundColor: isSelectedDay 
                                                                        ? 'rgba(25, 118, 210, 0.08)' 
                                                                        : 'rgba(0, 0, 0, 0.02)',
                                                                    border: isSelectedDay 
                                                                        ? '1px solid rgba(25, 118, 210, 0.3)' 
                                                                        : '1px solid rgba(0, 0, 0, 0.1)',
                                                                    borderRadius: 1,
                                                                    textAlign: 'center'
                                                                }}
                                                            >
                                                                <Typography 
                                                                    variant="caption" 
                                                                    sx={{ 
                                                                        fontWeight: 'bold', 
                                                                        color: isSelectedDay ? 'primary.main' : 'text.primary',
                                                                        fontSize: '0.7rem',
                                                                        display: 'block',
                                                                        mb: 0.2
                                                                    }}
                                                                >
                                                                    {translatedDayName}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize: '0.6rem',
                                                                        fontWeight: 500,
                                                                        color: hours.ouverture === 'fermé' ? 'error.main' : 'text.primary',
                                                                        display: 'block'
                                                                    }}
                                                                >
                                                                    {hours.ouverture === 'fermé' ? t('pro_team_member.add_schedule.closed') : `${hours.ouverture} - ${hours.fermeture}`}
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                )}
                            </>
                        )}
                        {formContent}
                    </Box>
                    <Popover
                        open={Boolean(timePickerAnchor)}
                        anchorEl={timePickerAnchor}
                        onClose={handleTimePickerClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            sx: {
                                p: 2,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                borderRadius: 2,
                                border: '1px solid #e0e0e0'
                            }
                        }}
                    >
                        {activeTimePicker && (
                            <Box>
                                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: '#666' }}>
                                    {activeTimePicker.timeType === 'break_start' ? t('pro_team_member.add_schedule.select_break_start') : 
                                    activeTimePicker.timeType === 'break_end' ? t('pro_team_member.add_schedule.select_break_end') :
                                    activeTimePicker.timeType === 'start_time' ? t('pro_team_member.add_schedule.select_start') : t('pro_team_member.add_schedule.select_end')}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', width: 60, textAlign: 'center' }}>
                                        {t('pro_team_member.add_schedule.hour')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', width: 60, textAlign: 'center' }}>
                                        {t('pro_team_member.add_schedule.minute')}
                                    </Typography>
                                </Box>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MultiSectionDigitalClock
                                        value={(() => {
                                            let timeValue = '';
                                            if (activeTimePicker.timeType === 'break_start') {
                                                timeValue = breakData[activeTimePicker.dayName]?.start_time || '';
                                            } else if (activeTimePicker.timeType === 'break_end') {
                                                timeValue = breakData[activeTimePicker.dayName]?.end_time || '';
                                            } else {
                                                const times = formData.schedule_times?.[activeTimePicker.dayName] || { start_time: '', end_time: '' };
                                                timeValue = times[activeTimePicker.timeType] || '';
                                            }
                                            
                                            if (timeValue && timeValue !== 'fermé') {
                                                try {
                                                    return dayjs(`2023-01-01T${timeValue}`);
                                                } catch {
                                                    return dayjs().startOf('day').hour(9);
                                                }
                                            }
                                            return dayjs().startOf('day').hour(9);
                                        })()
                                        }
                                        onChange={(newValue) => {
                                            if (activeTimePicker.timeType === 'break_start' || activeTimePicker.timeType === 'break_end') {
                                                // Handle break time changes
                                                const breakTimeType = activeTimePicker.timeType === 'break_start' ? 'start_time' : 'end_time';
                                                const timeValue = newValue ? 
                                                    (typeof newValue === 'string' ? newValue : newValue.format('HH:mm')) : 
                                                    '';
                                                
                                                setBreakData(prev => ({
                                                    ...prev,
                                                    [activeTimePicker.dayName]: {
                                                        ...prev[activeTimePicker.dayName],
                                                        [breakTimeType]: timeValue
                                                    }
                                                }));
                                            } else {
                                                // Handle regular schedule time changes
                                                handleTimeChange(activeTimePicker.dayName, activeTimePicker.timeType, newValue);
                                            }
                                        }}
                                        ampm={false}
                                        views={['hours', 'minutes']}
                                        timeSteps={{ hours: 1, minutes: 1 }}
                                        sx={{
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            '& .MuiMultiSectionDigitalClock-root': {
                                                maxWidth: 200
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        )}
                    </Popover>
                </>
            }
            existAction={true}
            actionTitle={isCreating ? t('pro_team_member.add_schedule.creating') : t('pro_team_member.add_schedule.create_schedule')}
            actionFunction={handleSubmit}
            disableAction={isCreating || selectedDays.length === 0}
            fullScreen={isFullScreen}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: isFullScreen ? '100%' : 1400,
                    width: isFullScreen ? '100%' : 'calc(100% - 48px)',
                    maxHeight: isFullScreen ? '100%' : '90vh',
                    height: isFullScreen ? '100%' : 'auto',
                    margin: isFullScreen ? 0 : 3,
                    borderRadius: isFullScreen ? 0 : 3,
                    overflow: 'auto'
                },
                '& .MuiButton-root': {
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    padding: { xs: '6px 12px', md: '8px 16px' }
                }
            }}
        />
    );
};

export default AddSchedule;